import React, { useEffect, useMemo, useState, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SectionReveal } from './ui/SectionReveal';
import { FileText, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
type UnitType = 'Apartamento' | 'Adosado';
interface Unit {
  ref: string;
  type: UnitType;
  bedrooms: number;
  bathrooms: number;
  area: number;
  terrace: number;
  price: number;
  plan?: string;
}
const units: Unit[] = [{
  ref: 'BC',
  type: 'Apartamento',
  bedrooms: 3,
  bathrooms: 2,
  area: 89.58,
  terrace: 30.76,
  price: 330000,
  plan: 'https://cdn.magicpatterns.com/uploads/5dRLT3Ks8SAu8xETYuisdr/Screenshot_2026-06-11_at_16.51.26.png'
}, {
  ref: '1C',
  type: 'Apartamento',
  bedrooms: 3,
  bathrooms: 2,
  area: 89.58,
  terrace: 13.06,
  price: 325500,
  plan: 'https://cdn.magicpatterns.com/uploads/iKiXms4HQHGpYGXk6KnxZB/Screenshot_2026-06-11_at_17.01.10.png'
}, {
  ref: 'V10',
  type: 'Adosado',
  bedrooms: 3,
  bathrooms: 3,
  area: 189.42,
  terrace: 48.93,
  price: 435000,
  plan: 'https://cdn.magicpatterns.com/uploads/ocRuBPeb3bfbLgjXkmuieR/Screenshot_2026-06-11_at_17.02.06.png'
}, {
  ref: 'V11',
  type: 'Adosado',
  bedrooms: 3,
  bathrooms: 3,
  area: 189.42,
  terrace: 48.93,
  price: 435000,
  plan: 'https://cdn.magicpatterns.com/uploads/keAqHS76kLUWVsfXof55d6/Screenshot_2026-06-11_at_17.02.41.png'
}, {
  ref: 'V12',
  type: 'Adosado',
  bedrooms: 3,
  bathrooms: 3,
  area: 189.42,
  terrace: 48.93,
  price: 435000,
  plan: 'https://cdn.magicpatterns.com/uploads/2AGtbEBSVkpwuT2jg9Xujk/Screenshot_2026-06-11_at_17.03.14.png'
}, {
  ref: 'V13',
  type: 'Adosado',
  bedrooms: 3,
  bathrooms: 3,
  area: 189.42,
  terrace: 48.93,
  price: 435000,
  plan: 'https://cdn.magicpatterns.com/uploads/nRFqRvt3AUszguRCPLSgn4/Screenshot_2026-06-11_at_17.03.43.png'
}, {
  ref: 'V14',
  type: 'Adosado',
  bedrooms: 3,
  bathrooms: 3,
  area: 189.42,
  terrace: 48.93,
  price: 435000,
  plan: 'https://cdn.magicpatterns.com/uploads/wRbM42JhyLNxwTu6MHbZH9/Screenshot_2026-06-11_at_17.04.40.png'
}];
const typeFilters: Array<'Todos' | UnitType> = ['Todos', 'Apartamento', 'Adosado'];
const formatNumber = (n: number) => n.toLocaleString('es-ES', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
const formatPrice = (n: number) => n.toLocaleString('es-ES');
export function AvailabilitySection() {
  const {
    t
  } = useLanguage();
  const [typeFilter, setTypeFilter] = useState<'Todos' | UnitType>('Todos');
  const [activeUnit, setActiveUnit] = useState<Unit | null>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const scrollTable = (direction: 'left' | 'right') => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollBy({
        left: direction === 'left' ? -320 : 320,
        behavior: 'smooth'
      });
    }
  };
  const scrollTo = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveUnit(null);
    };
    if (activeUnit) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [activeUnit]);
  const filtered = useMemo(() => units.filter(u => typeFilter === 'Todos' || u.type === typeFilter), [typeFilter]);
  return <SectionReveal id="disponibilidad" className="bg-light" data-id="element-36">
      <div className="container mx-auto px-6 md:px-12" data-id="element-37">
        <div className="text-center mb-12" data-id="element-38">
          <h2 className="text-4xl md:text-6xl font-serif text-navy mb-5" data-id="element-39">
            {t.availability.title}
          </h2>
          <p className="text-navy/70 text-lg max-w-2xl mx-auto" data-id="element-40">
            {t.availability.subtitle}
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 justify-center mb-12" data-id="element-41">
          {typeFilters.map(filter => <button key={filter} onClick={() => setTypeFilter(filter)} className={`px-6 py-2 rounded-full text-sm font-serif transition-colors ${typeFilter === filter ? 'bg-sand text-navy' : 'bg-sand-light text-navy hover:bg-sand'}`} data-id="element-42">
              {filter === 'Apartamento' ? t.availability.filters.apartments : filter === 'Adosado' ? t.availability.filters.townhouses : t.availability.filters.all}
            </button>)}
        </div>

        {/* Table */}
        <div className="relative" data-id="element-43">
          {/* Fixed scroll arrows */}
          <button onClick={() => scrollTable('left')} className="absolute left-0 top-1/2 -translate-y-1/2 -ml-3 md:-ml-5 z-20 w-10 h-10 md:w-12 md:h-12 bg-navy hover:bg-navy-light text-white rounded-full flex items-center justify-center shadow-lg transition-colors" aria-label={t.availability.table.type} data-id="element-44">
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" data-id="element-45" />
          </button>
          <button onClick={() => scrollTable('right')} className="absolute right-0 top-1/2 -translate-y-1/2 -mr-3 md:-mr-5 z-20 w-10 h-10 md:w-12 md:h-12 bg-navy hover:bg-navy-light text-white rounded-full flex items-center justify-center shadow-lg transition-colors" aria-label={t.availability.table.plans} data-id="element-46">
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" data-id="element-47" />
          </button>

          <div ref={tableScrollRef} className="overflow-x-auto" data-id="element-48">
            <table className="w-full min-w-[760px] border-collapse" data-id="element-49">
              <thead data-id="element-50">
                <tr className="border-b-2 border-navy/20" data-id="element-51">
                  <th className="py-5 px-4 text-center text-lg font-serif font-bold text-navy" data-id="element-52">
                    {t.availability.table.type}
                  </th>
                  <th className="py-5 px-4 text-center text-lg font-serif font-bold text-navy" data-id="element-53">
                    {t.availability.table.beds}
                  </th>
                  <th className="py-5 px-4 text-center text-lg font-serif font-bold text-navy" data-id="element-54">
                    {t.availability.table.baths}
                  </th>
                  <th className="py-5 px-4 text-center text-lg font-serif font-bold text-navy" data-id="element-55">
                    {t.availability.table.area}
                  </th>
                  <th className="py-5 px-4 text-center text-lg font-serif font-bold text-navy" data-id="element-56">
                    {t.availability.table.terrace}
                  </th>
                  <th className="py-5 px-4 text-center text-lg font-serif font-bold text-navy" data-id="element-57">
                    {t.availability.table.price}
                  </th>
                  <th className="py-5 px-4 text-center text-lg font-serif font-bold text-navy" data-id="element-58">
                    {t.availability.table.plans}
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-59">
                {filtered.map((unit, index) => <tr key={unit.ref} className={`border-b border-navy/10 ${index % 2 === 1 ? 'bg-sand-light' : 'bg-white'}`} data-id="element-60">
                    <td className="py-6 px-4 text-center" data-id="element-61">
                      <p className="text-lg text-navy font-medium" data-id="element-62">
                        {unit.ref}
                      </p>
                      <p className="text-xs uppercase tracking-wider text-navy/50" data-id="element-63">
                        {unit.type === 'Apartamento' ? t.availability.filters.apartments : t.availability.filters.townhouses}
                      </p>
                    </td>
                    <td className="py-6 px-4 text-center text-lg text-navy" data-id="element-64">
                      {unit.bedrooms}
                    </td>
                    <td className="py-6 px-4 text-center text-lg text-navy" data-id="element-65">
                      {unit.bathrooms}
                    </td>
                    <td className="py-6 px-4 text-center text-lg text-navy whitespace-nowrap" data-id="element-66">
                      {formatNumber(unit.area)} m²
                    </td>
                    <td className="py-6 px-4 text-center text-lg text-navy whitespace-nowrap" data-id="element-67">
                      {formatNumber(unit.terrace)} m²
                    </td>
                    <td className="py-6 px-4 text-center text-lg text-navy font-semibold whitespace-nowrap" data-id="element-68">
                      {formatPrice(unit.price)} €
                    </td>
                    <td className="py-6 px-4 text-center" data-id="element-69">
                      <button onClick={() => unit.plan ? setActiveUnit(unit) : scrollTo('#contacto')} className="inline-flex items-center gap-2 bg-navy text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-navy-light transition-colors" data-id="element-70">
                        <FileText className="w-4 h-4" data-id="element-71" />
                        {unit.plan ? t.availability.buttons.viewPlan : t.availability.buttons.reqPlan}
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        {filtered.length === 0 && <p className="text-center text-navy/60 py-12 font-serif text-lg" data-id="element-72">
            {t.availability.empty}
          </p>}
      </div>

      {/* Plan viewer */}
      <AnimatePresence data-id="element-73">
        {activeUnit && activeUnit.plan && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center bg-navy/80 backdrop-blur-sm p-4 md:p-8" onClick={() => setActiveUnit(null)} role="dialog" aria-modal="true" aria-label={`${t.availability.modal.unit} ${activeUnit.ref}`} data-id="element-74">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 12
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 12
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-75">
              <div className="flex items-center justify-between px-6 py-4 border-b border-navy/10" data-id="element-76">
                <div data-id="element-77">
                  <h3 className="font-serif text-xl text-navy" data-id="element-78">
                    {t.availability.modal.unit} {activeUnit.ref}
                  </h3>
                  <p className="text-xs uppercase tracking-wider text-navy/50" data-id="element-79">
                    {activeUnit.type === 'Apartamento' ? t.availability.filters.apartments : t.availability.filters.townhouses}{' '}
                    · {formatNumber(activeUnit.area)} m²
                  </p>
                </div>
                <button onClick={() => setActiveUnit(null)} aria-label={t.availability.modal.close} className="p-2 rounded-full text-navy hover:bg-light transition-colors" data-id="element-80">
                  <X className="w-5 h-5" data-id="element-81" />
                </button>
              </div>
              <div className="overflow-auto p-4 md:p-8 bg-light/40" data-id="element-82">
                <img src={activeUnit.plan} alt={`${t.availability.modal.unit} ${activeUnit.ref}`} className="w-full h-auto object-contain mx-auto" data-id="element-83" />
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </SectionReveal>;
}